import { infiniteQueryOptions, keepPreviousData, queryOptions } from '@tanstack/react-query';
import { api, type PaginationResponse } from '@/api';
import type { ReviewIdentity } from '@/features/user-review/types';
import type {
  InsightTaskLinks,
  Task,
  TaskActivity,
  TaskDetail,
  TaskFinding,
  TaskListFilters,
} from '@/features/tasks/types';

export const tasksKey = ['tasks'] as const;
export const tasksQuery = (filters: TaskListFilters & { pageSize?: number }) =>
  infiniteQueryOptions({
    queryKey: [...tasksKey, 'list', filters],
    queryFn: async ({ pageParam }) =>
      (
        await api.get<PaginationResponse<Task>>('v1/tasks', {
          params: { pageSize: 30, ...filters, page: pageParam },
        })
      ).data,
    initialPageParam: 1,
    getNextPageParam: (last, pages) =>
      pages.length < last.pageCount ? pages.length + 1 : undefined,
    placeholderData: keepPreviousData,
  });
export const activeTasksCountQuery = (
  scope: Pick<TaskListFilters, 'organizationId' | 'projectId' | 'workspaceId'>,
) =>
  infiniteQueryOptions({
    ...tasksQuery({ ...scope, status: 'open,in_progress', includeCovered: false, pageSize: 1 }),
    select: (data) => data.pages[0]?.totalCount ?? 0,
    placeholderData: undefined,
  });
export const taskQuery = (taskId: string) =>
  queryOptions({
    queryKey: [...tasksKey, 'detail', taskId],
    queryFn: async ({ client }) => {
      const { data } = await api.get<TaskDetail>(`v1/tasks/${taskId}`);
      if (data.taskId !== taskId) client.setQueryData([...tasksKey, 'detail', data.taskId], data);
      return data;
    },
  });
export const taskFindingsQuery = (taskId: string) =>
  queryOptions({
    queryKey: [...tasksKey, 'findings', taskId],
    queryFn: async () =>
      (
        await api.get<{ data: TaskFinding[] }>(`v1/tasks/${taskId}/findings`, {
          params: { page_size: 100 },
        })
      ).data.data,
  });
export const taskActivityQuery = (taskId: string) =>
  infiniteQueryOptions({
    queryKey: [...tasksKey, 'activity', taskId],
    queryFn: async ({ pageParam }) =>
      (
        await api.get<{
          data: TaskActivity[];
          totalCount: number;
          page: number;
          pageSize: number;
        }>(`v1/tasks/${taskId}/activity`, { params: { page: pageParam, pageSize: 30 } })
      ).data,
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.totalCount ? last.page + 1 : undefined,
  });
export const insightTaskLinksQuery = (id: string) =>
  queryOptions({
    queryKey: [...tasksKey, 'insightLinks', id],
    queryFn: async () => (await api.get<InsightTaskLinks>(`v1/tasks/insight-links/${id}`)).data,
  });

export const findingTaskQuery = (
  identity: Pick<ReviewIdentity, 'subjectKind' | 'subjectId' | 'resourceId'>,
) =>
  queryOptions({
    queryKey: [...tasksKey, 'findingTask', identity],
    queryFn: async () => {
      const response = await api.get<{ taskId: string }>(
        `v1/tasks/for-finding/${identity.subjectKind}/${identity.subjectId}`,
        {
          params: { resourceId: identity.resourceId },
          validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
        },
      );
      return response.status === 404 ? null : response.data;
    },
  });
